/* ==========================================================================
   Vanguarda quick-add — pick a size and colour without leaving the cart.

   The problem it solves: a suggested product with variants had a "Choose
   options" link to the product page. Following it threw away the cart drawer,
   the reward ladder and the rest of the outfit just to choose a size, and the
   shopper had to find their way back. Everything below exists to keep them on
   the one screen where they were already deciding to spend money.

   Data comes from /products/<handle>.js on click rather than being embedded in
   the cart HTML. The cart can offer three suggestions, each with a dozen
   variants; serialising all of that into every cart render — and re-sending it
   on every quantity change, since the drawer re-renders itself — would cost
   far more than one small request made only when somebody actually asks.

   Degrades honestly: the trigger is a real <a href> to the product page, so
   with JavaScript off, or if the fetch fails, the link does what it always did.
   ========================================================================== */
(function () {
  'use strict';

  var root = null;      // #QuickAdd
  var body = null;      // #QuickAddBody
  var product = null;   // the fetched product JSON
  var chosen = [];      // selected value per option index
  var opener = null;    // the element that opened the sheet, for focus return
  var cache = {};       // handle -> product, so re-opening is instant

  function $(sel, scope) { return (scope || document).querySelector(sel); }

  function money(cents) {
    // Shopify hands the theme its own money format; reuse it rather than
    // inventing a second currency renderer that will disagree with the cart.
    var fmt = (window.VG && window.VG.moneyFormat) || '${{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      var v = cents / 100;
      switch (name) {
        case 'amount': return v.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d),)/g, '.');
        case 'amount_no_decimals': return String(Math.round(v));
        case 'amount_with_comma_separator': return v.toFixed(2).replace('.', ',');
        case 'amount_with_period_separator': return v.toFixed(2);
        case 'amount_no_decimals_with_comma_separator': return String(Math.round(v));
        case 'amount_no_decimals_with_space_separator': return String(Math.round(v));
        case 'amount_with_space_separator': return v.toFixed(2).replace('.', ',');
        default: return v.toFixed(2);
      }
    });
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Shopify has shipped `options` as both ["Color","Size"] and
     [{name:"Color", values:[...]}] depending on the API version the shop is on.
     Normalise once here rather than guessing at every use site. */
  function optionNames(p) {
    if (!p.options || !p.options.length) return [];
    return p.options.map(function (o) { return typeof o === 'string' ? o : o.name; });
  }
  function optionValues(p, i) {
    var seen = [], out = [];
    p.variants.forEach(function (v) {
      var val = v['option' + (i + 1)];
      if (val && seen.indexOf(val) === -1) { seen.push(val); out.push(val); }
    });
    return out;
  }

  function isColour(name) {
    return /colou?r|cor|colore|farbe/i.test(name || '');
  }

  /* A variant matches when every option the shopper has chosen agrees. Options
     they have not chosen yet are wildcards, which is what makes it possible to
     ask "is any Medium available in Navy?" before a size is picked. */
  function matches(variant, sel) {
    for (var i = 0; i < sel.length; i++) {
      if (sel[i] != null && variant['option' + (i + 1)] !== sel[i]) return false;
    }
    return true;
  }

  function currentVariant() {
    if (!product) return null;
    var names = optionNames(product);
    if (chosen.length < names.length) return null;
    for (var i = 0; i < names.length; i++) if (chosen[i] == null) return null;
    for (var v = 0; v < product.variants.length; v++) {
      if (matches(product.variants[v], chosen)) return product.variants[v];
    }
    return null;
  }

  /* Availability shown per value, holding the OTHER chosen options fixed. Pick
     Navy and the sizes redraw to show what is actually in stock in Navy —
     without this the shopper picks a size, presses add, and gets told no. */
  function valueAvailable(optIndex, value) {
    var probe = chosen.slice();
    probe[optIndex] = value;
    // ignore options after this one: they are what the shopper picks next
    for (var i = optIndex + 1; i < probe.length; i++) probe[i] = null;
    return product.variants.some(function (v) {
      return v.available && matches(v, probe);
    });
  }

  /* An image for a colour value: the first variant wearing it that has its own
     photo. A real garment chip beats a hex guess, and needs no colour table to
     be kept in sync with the catalogue. */
  function valueImage(optIndex, value) {
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (v['option' + (optIndex + 1)] === value && v.featured_image && v.featured_image.src) {
        return v.featured_image.src;
      }
    }
    return null;
  }

  function sized(url, w) {
    if (!url) return '';
    return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, '_' + w + 'x$1$2');
  }

  /* ------------------------------------------------------------------ draw */
  function draw() {
    var names = optionNames(product);
    var variant = currentVariant();
    var shown = variant || product.variants[0];
    var img = (variant && variant.featured_image && variant.featured_image.src) ||
              product.featured_image || '';
    var price = shown ? shown.price : product.price;
    var compare = shown ? shown.compare_at_price : product.compare_at_price;

    var h = '' +
      '<div class="qa__top">' +
        (img ? '<img class="qa__img" src="' + esc(sized(img, 200)) + '" alt="" width="72" height="90">' : '') +
        '<div>' +
          '<p class="qa__title" id="QuickAddTitle">' + esc(product.title) + '</p>' +
          '<p class="qa__price">' +
            (compare && compare > price ? '<s>' + money(compare) + '</s>' : '') +
            money(price) +
          '</p>' +
        '</div>' +
      '</div>';

    names.forEach(function (name, i) {
      var values = optionValues(product, i);
      // A single-value option is not a choice; showing it is just noise.
      if (values.length < 2) { chosen[i] = values[0] || null; return; }
      var swatch = isColour(name);

      h += '<div class="qa__opt"><div class="qa__lab">' +
             '<b>' + esc(name) + '</b>' +
             '<i>' + esc(chosen[i] || 'Select') + '</i>' +
           '</div><div class="qa__vals">';

      values.forEach(function (val) {
        var ok = valueAvailable(i, val);
        var on = chosen[i] === val;
        /* The circular treatment is earned by having an image to put in it.
           It used to be applied to every colour value, and when a variant had
           no featured_image the button kept the 38px circle, got no
           background, and hid its own label -- a blank disc the shopper had no
           way to read. The theme's colour palette lives in Liquid and this
           sheet is built in JS from /products/<handle>.js, which carries no
           swatch data, so there is nothing here to paint it with. A plain
           named button is the honest answer. */
        var vi = swatch ? valueImage(i, val) : null;
        var asSwatch = swatch && !!vi;
        var cls = 'qa__val' + (asSwatch ? ' qa__val--sw' : '') + (on ? ' is-on' : '') + (ok ? '' : ' is-out');
        var style = asSwatch ? ' style="background-image:url(' + esc(sized(vi, 80)) + ')"' : '';
        h += '<button type="button" class="' + cls + '"' + style +
             ' data-opt="' + i + '" data-val="' + esc(val) + '"' +
             (ok ? '' : ' disabled') +
             ' aria-pressed="' + (on ? 'true' : 'false') + '">' +
             (asSwatch ? '<span class="visually-hidden">' + esc(val) + '</span>' : esc(val)) +
             '</button>';
      });
      h += '</div></div>';
    });

    var ready = !!(variant && variant.available);
    var label;
    if (variant && !variant.available) label = 'Sold out';
    else if (!variant) label = 'Select ' + esc(nextUnchosen(names) || 'options');
    else label = 'Add · ' + money(price);

    h += '<div class="qa__cta"><button type="button" class="button button--full button--lg" ' +
         'data-qa-add' + (ready ? '' : ' disabled') + '>' + label + '</button></div>';

    body.innerHTML = h;
  }

  function nextUnchosen(names) {
    for (var i = 0; i < names.length; i++) {
      if (chosen[i] == null && optionValues(product, i).length > 1) return names[i].toLowerCase();
    }
    return null;
  }

  /* ------------------------------------------------------------- confirmed */
  function drawAdded(variant) {
    var bits = [];
    optionNames(product).forEach(function (n, i) {
      if (chosen[i]) bits.push(chosen[i]);
    });
    body.innerHTML = '' +
      '<div class="qa__done">' +
        '<span class="qa__tick" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>' +
        '</span>' +
        '<p class="qa__done-t">Added to your bag</p>' +
        '<p class="qa__done-s"><b>' + esc(product.title) + '</b><br>' +
          esc(bits.join(' / ')) + (bits.length ? ' · ' : '') + money(variant.price) + '</p>' +
        '<div class="qa__done-btns">' +
          '<button type="button" class="button button--full button--outline" data-qa-close>Keep shopping</button>' +
          '<button type="button" class="button button--full" data-qa-checkout>Checkout</button>' +
        '</div>' +
      '</div>';
    var first = $('[data-qa-close]', body);
    if (first) first.focus();
  }

  /* ------------------------------------------------------------ open/close */
  function open(handle, trigger) {
    root = document.getElementById('QuickAdd');
    if (!root) return false;
    body = document.getElementById('QuickAddBody');
    opener = trigger || null;

    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    // next frame, so the transition has a start state to move from
    requestAnimationFrame(function () { root.classList.add('is-open'); });
    body.innerHTML = '<div class="qa__loading" role="status">Loading…</div>';

    if (cache[handle]) { setProduct(cache[handle]); return true; }

    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root + 'products/' + handle + '.js'
            : '/products/' + handle + '.js')
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (p) { cache[handle] = p; setProduct(p); })
      .catch(function () {
        // Never strand the shopper in a sheet that cannot do anything: close it
        // and let the original link carry them to the product page.
        close();
        if (opener && opener.getAttribute('href')) window.location.href = opener.href;
      });
    return true;
  }

  function setProduct(p) {
    product = p;
    chosen = optionNames(p).map(function () { return null; });
    // One variant, or one that is the only one in stock: pre-select it so the
    // shopper presses one button instead of two.
    var live = p.variants.filter(function (v) { return v.available; });
    if (live.length === 1) {
      optionNames(p).forEach(function (_, i) { chosen[i] = live[0]['option' + (i + 1)]; });
    }
    draw();
    var f = body.querySelector('button:not([disabled])');
    if (f) f.focus();
  }

  function close() {
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    var el = root;
    setTimeout(function () { el.hidden = true; }, 340);
    product = null;
    if (opener && document.contains(opener)) opener.focus();
    opener = null;
  }

  /* ----------------------------------------------------------------- wire */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-quick-view]');
    if (trigger) {
      // Modified clicks still belong to the browser.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (open(trigger.getAttribute('data-quick-view'), trigger)) e.preventDefault();
      return;
    }

    if (!root || root.hidden) return;

    if (e.target.closest('[data-qa-close]')) { e.preventDefault(); close(); return; }
    if (e.target.closest('[data-qa-checkout]')) {
      e.preventDefault();
      var form = document.getElementById('cart-form');
      if (form) form.submit();
      else window.location.href = (window.VG && window.VG.routes && window.VG.routes.cart_url) || '/cart';
      return;
    }

    var val = e.target.closest('[data-val]');
    if (val && !val.disabled) {
      var i = Number(val.getAttribute('data-opt'));
      chosen[i] = val.getAttribute('data-val');
      // Changing an earlier option can invalidate a later one — drop anything
      // downstream that the new combination does not actually stock.
      for (var j = i + 1; j < chosen.length; j++) {
        if (chosen[j] != null && !valueAvailable(j, chosen[j])) chosen[j] = null;
      }
      draw();
      return;
    }

    var add = e.target.closest('[data-qa-add]');
    if (add && !add.disabled) {
      e.preventDefault();
      var variant = currentVariant();
      if (!variant || !window.vgCart) return;
      add.setAttribute('aria-disabled', 'true');
      window.vgCart.add([{ id: variant.id, quantity: 1 }])
        .then(function () { drawAdded(variant); })
        .catch(function () {
          add.removeAttribute('aria-disabled');
          add.textContent = 'Could not add — try again';
        });
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root && !root.hidden) close();
  });

  window.VGQuickAdd = { open: open, close: close };
})();
